import {
  IconButton,
  type IconButtonProps,
} from "@-/dev.improve.haniverse.components.ui.button.icon";
import { cn } from "@-/com.npmjs.cn";
import {
  ToolbarGlassSurface,
  useToolbarGridMetrics,
} from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";

export type ToolbarIconButtonProps = Omit<IconButtonProps, "active" | "children"> & {
  /** Match the closed More trigger while its menu is loading. */
  size?: "default" | "menu";
};

/**
 * Small glass circle button holding a single icon — the always-circular controls on the
 * right of the Browse toolbars (filter / sort / view-mode / return-to-current-level). A thin
 * pass-through over the canonical {@link IconButton}: same circle style, minus the `active`
 * (pushed-in) toggle and custom-children affordances the toolbars don't use.
 *
 * Pass `label` to render an icon-and-text pill instead of a circle — the shape the expansion menus
 * use so each revealed action names itself. Its height follows the expanded tray's grid metrics;
 * `IconButton` supplies the label spacing and appearance.
 *
 * Extra `Button` props are forwarded so this can be used as a `DropdownMenuTrigger asChild` child
 * (the trigger injects `onPress`/ref/accessibility onto it).
 */
export function ToolbarIconButton({
  className,
  label,
  size = "default",
  style,
  ...props
}: ToolbarIconButtonProps) {
  const { diameter, expandedControlDiameter } = useToolbarGridMetrics();
  const controlStyle =
    size === "menu"
      ? { height: diameter, width: diameter }
      : label === undefined
        ? undefined
        : { height: expandedControlDiameter };
  return (
    <ToolbarGlassSurface
      // Keep UIKit's pressed glass halo inside the circle/pill. Otherwise it extends beyond
      // the control and the toolbar edge cuts a straight streak across its lower half.
      className={cn("overflow-hidden", size === "menu" ? "ml-0.5" : undefined)}
    >
      <IconButton
        className={cn(
          label === undefined ? "size-9" : undefined,
          // The glass supplies the material, so clear IconButton's own surface — but before the
          // caller's classes, so a caller's deliberate tint (e.g. a selected section's
          // `bg-accent`) still paints instead of being merged away.
          "border-0 bg-transparent shadow-none hover:bg-transparent active:bg-transparent dark:bg-transparent dark:hover:bg-transparent dark:active:bg-transparent",
          className,
        )}
        label={label}
        style={
          controlStyle === undefined
            ? style
            : typeof style === "function"
              ? (state) => [controlStyle, style(state)]
              : [controlStyle, style]
        }
        {...props}
      />
    </ToolbarGlassSurface>
  );
}
